import { useEffect, useMemo, useState } from "react";
import { ScreenerQuestionAnwerResponse } from "@/services";
import {
  AnswerRow,
  FreeTextAudience,
  FreeTextValues,
  QuestionValue,
  ScreenerQuestion,
} from "../assessment-types";
import {
  answersById,
  resolveScreener,
  isVisible,
  questionIssue,
  questionsFor,
  toAnswerRow,
  valuesFromRows,
} from "../free-text-screening";

// Form state for a free-text screener: prefilled from saved rows, submitted as
// one entry per answered question.
export const useFreeTextScreener = (
  type: string | null | undefined,
  questions: ScreenerQuestion[] | undefined,
  prefillRows: ScreenerQuestionAnwerResponse[] | undefined,
  audience?: FreeTextAudience,
  answerOptions?: string[]
) => {
  const def = useMemo(
    () => resolveScreener(type, questions, answerOptions),
    [type, questions, answerOptions]
  );
  const [values, setValues] = useState<FreeTextValues>({});
  const [savedValues, setSavedValues] = useState<FreeTextValues>({});

  useEffect(() => {
    if (!def || !questions) return;
    const prefilled = valuesFromRows(def, questions, prefillRows ?? []);
    setValues(prefilled);
    setSavedValues(prefilled);
  }, [def, questions, prefillRows]);

  const isDirty = JSON.stringify(values) !== JSON.stringify(savedValues);

  // Hidden questions lose their answers, not just their display.
  const setValue = (uuid: string, value: QuestionValue) =>
    setValues((prev) => {
      const next = { ...prev, [uuid]: value };
      if (!def || !questions) return next;
      const byId = answersById(def, questions, next);
      questionsFor(def, questions).forEach(({ question, q }) => {
        if (!isVisible(q, byId) && next[question.uuid!])
          delete next[question.uuid!];
      });
      return next;
    });

  const isEmpty = !Object.values(values).some(
    (v) => v.answers.length || v.notes.trim()
  );

  const byId = def && questions ? answersById(def, questions, values) : {};
  const issue =
    def && questions
      ? (questionsFor(def, questions, audience)
          .filter(({ q }) => isVisible(q, byId))
          .map(({ question, q }) =>
            questionIssue(
              q,
              q.label ?? question.question,
              values[question.uuid!]
            )
          )
          .find(Boolean) ?? "")
      : "";

  const toRows = (): AnswerRow[] =>
    questionsFor(def!, questions ?? [], audience).flatMap(({ question, q }) => {
      if (!isVisible(q, byId)) return [];
      const row = values[question.uuid!]
        ? toAnswerRow(question, values[question.uuid!])
        : undefined;
      return row ? [row] : [];
    });

  return { def, values, setValue, isEmpty, isDirty, issue, toRows };
};
